import AppCard from '@nxsys/components/AppCard';
import AppIconCard from '@nxsys/components/AppIconCard';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useGetStatisticsCompliance,
  useGetStatisticsEmployeeSubcontractor,
} from '@nxsys/hooks/api/dashboard';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { Icons } from '@nxsys/types/icons';
import { Col, Row } from 'antd';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useEffect, useMemo, useState } from 'react';
import EmployeeModal, {
  EmployeeModalTypeEnum,
} from './components/EmployeeModal';
import AssignmentModal, {
  AssignmentModalTypeEnum,
} from './components/AssignmentModal';
import SafeRecErrorModal from './components/SafeRecErrorModal';
import VeriPayeErrorModal from './components/VeriPayeErrorModal';
import clsx from 'clsx';

const EmployeeSubcontractor = () => {
  const { messages } = useTypedIntl();
  const { value, loading, run } = useGetStatisticsEmployeeSubcontractor();
  const {
    value: complianceValue,
    loading: complianceLoading,
    run: runCompliance,
  } = useGetStatisticsCompliance();
  const navigate = useNavigateWithQuery();
  const data = useMemo(() => value?.data, [value]);
  const complianceData = useMemo(
    () => complianceValue?.data,
    [complianceValue],
  );

  const [isOpenEmployeeModal, setIsOpenEmployeeModal] = useState(false);
  const [employeeModalType, setEmployeeModalType] =
    useState<EmployeeModalTypeEnum | null>(null);

  const [isOpenAssignmentModal, setIsOpenAssignmentModal] = useState(false);
  const [assignmentModalType, setAssignmentModalType] =
    useState<AssignmentModalTypeEnum | null>(null);

  const [isOpenSafeRecErrorModal, setIsOpenSafeRecErrorModal] = useState(false);
  const [isOpenVeriPayeErrorModal, setIsOpenVeriPayeErrorModal] =
    useState(false);

  useEffect(() => {
    run();
    runCompliance(undefined);
  }, []);

  const handleOpenEmployeeModal = (type: EmployeeModalTypeEnum) => {
    setEmployeeModalType(type);
    setIsOpenEmployeeModal(true);
  };

  const handleOpenAssignmentModal = (type: AssignmentModalTypeEnum) => {
    setAssignmentModalType(type);
    setIsOpenAssignmentModal(true);
  };

  const itemsSubcontractor = [
    {
      title: data?.subcontractors?.totalSubcontractorsRegistrations,
      description: 'Pending Registration',
      iconColor: defaultTheme.theme.palette.warning[300],
      iconType: 'solid',
      onClick: () =>
        handleOpenEmployeeModal(EmployeeModalTypeEnum.PENDING_REGISTRATION),
    },
    {
      title: data?.subcontractors?.totalSubcontractorsOnHold,
      description: 'On Hold',
      iconColor: defaultTheme.theme.palette.error[300],
      iconType: 'outlined',
      onClick: () => handleOpenEmployeeModal(EmployeeModalTypeEnum.ON_HOLD),
    },
  ];

  const itemsAssignment = [
    {
      title: data?.assignments?.totalAssignmentsActive,
      description: 'Active Assignments',
      onClick: () => handleOpenAssignmentModal(AssignmentModalTypeEnum.ACTIVE),
    },
    {
      title: data?.assignments?.totalAssignmentsNearingCompletion,
      description: 'Nearing Completion',
      onClick: () =>
        handleOpenAssignmentModal(AssignmentModalTypeEnum.NEARING_COMPLETION),
    },
  ];

  const safeRecStats = complianceData?.safeRec;
  const veriPayeStats = complianceData?.veriPaye;
  // Only surface a tile when the tenant actually has compliance activity —
  // an integrated company with eligible payrolls or outstanding errors.
  // Otherwise non-integrated / non-umbrella tenants would see empty "0" tiles.
  const hasSafeRec = !!safeRecStats && safeRecStats.eligible + safeRecStats.errors > 0;
  const hasVeriPaye =
    !!veriPayeStats && veriPayeStats.eligible + veriPayeStats.errors > 0;

  const itemsCompliance = [
    ...(hasSafeRec
      ? [
          {
            title: safeRecStats!.eligible,
            description: 'SafeRec',
            errorCount: safeRecStats!.errors,
            onClickError: () => setIsOpenSafeRecErrorModal(true),
            onClick: () => navigate('/reports/payroll/payment-summary'),
          },
        ]
      : []),
    ...(hasVeriPaye
      ? [
          {
            title: veriPayeStats!.eligible,
            description: 'VeriPaye',
            errorCount: veriPayeStats!.errors,
            onClickError: () => setIsOpenVeriPayeErrorModal(true),
            onClick: () => navigate('/reports/payroll/payment-summary'),
          },
        ]
      : []),
  ];

  const needShowCompliance = hasSafeRec || hasVeriPaye;

  return (
    <div className="OnHold">
      <AppCard className="p-[24px]" loading={loading}>
        <div className="flex justify-between items-center mb-[24px]">
          <AppIconCard
            iconName="IconUserCircle"
            title={messages['common.employees']}
          />
        </div>
        <Row gutter={[16, 16]}>
          <Col sm={24} md={12}>
            <div
              className="border border-[#CDDDD9] dashboard-hover-card bg-[#F9FBFA] rounded-[8px] p-[16px]"
              onClick={() =>
                handleOpenEmployeeModal(EmployeeModalTypeEnum.TOTAL)
              }
            >
              <div className="flex items-center justify-between gap-x-[8px] mb-[18px]">
                <div className="bg-violet-100 rounded-[8px] p-[8px]">
                  <Icons.IconUserGroup
                    color={defaultTheme.theme.palette.violet[500]}
                  />
                </div>
                <Icons.IconArrowRight />
              </div>
              <div className="text-[22px] font-bold mb-[8px]">
                {data?.subcontractors?.totalSubcontractors}
              </div>
              <div className="text-[14px] font-bold">
                {messages['common.totalEmployees']}
              </div>
            </div>
          </Col>
          <Col sm={24} md={12}>
            <div className="space-y-[16px]">
              {itemsSubcontractor.map((item) => (
                <div
                  key={item.title}
                  className="border border-[#CDDDD9] dashboard-hover-card rounded-[8px] p-[16px] flex items-center w-full gap-x-[12px]"
                  onClick={item.onClick}
                >
                  <div
                    className={clsx('rounded-full w-[12px] h-[12px]', {
                      'bg-warning-300': item.iconType === 'solid',
                      'border-[3px] border-error-300':
                        item.iconType === 'outlined',
                    })}
                  />
                  <div className="flex gap-x-[8px] flex-1 items-center">
                    <div className="text-[22px] font-bold">{item.title}</div>
                    <div className="text-[14px] font-bold">
                      {item.description}
                    </div>
                  </div>
                  <Icons.IconArrowRight />
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </AppCard>
      <Row gutter={[16, 16]}>
        <Col sm={24} md={needShowCompliance ? 12 : 24}>
          <AppCard className="p-[24px] mt-[16px]" loading={loading}>
            <div className="flex justify-between items-center mb-[24px]">
              <AppIconCard iconName="IconFileTextEdit" title="Assignments" />
            </div>
            <div className="space-y-[16px]">
              {itemsAssignment.map((item) => (
                <div
                  key={item.title}
                  className="border border-[#CDDDD9] dashboard-hover-card rounded-[8px] p-[16px] flex items-center w-full gap-x-[12px]"
                  onClick={item.onClick}
                >
                  <div className="border-[3px] rounded-full w-[12px] h-[12px] border-neutral-500" />
                  <div className="flex gap-x-[8px] flex-1 items-center">
                    <div className="text-[22px] font-bold">{item.title}</div>
                    <div className="text-[14px] font-bold">
                      {item.description}
                    </div>
                  </div>
                  <Icons.IconArrowRight />
                </div>
              ))}
            </div>
          </AppCard>
        </Col>
        {needShowCompliance && (
          <Col sm={24} md={12}>
            <AppCard className="p-[24px] mt-[16px]" loading={complianceLoading}>
              <div className="flex justify-between items-center mb-[24px]">
                <AppIconCard iconName="IconFileTextEdit" title="Compliance" />
              </div>
              <div className="space-y-[16px]">
                {itemsCompliance.map((item) => (
                  <div
                    key={item.description}
                    className="border border-[#CDDDD9] dashboard-hover-card rounded-[8px] p-[16px] flex items-center w-full gap-x-[12px] cursor-pointer"
                    onClick={item.onClick}
                  >
                    <div className="border-[3px] rounded-full w-[12px] h-[12px] border-neutral-500 flex-shrink-0" />
                    <div className="flex gap-x-[8px] flex-1 items-center">
                      <div className="text-[22px] font-bold">{item.title}</div>
                      <div className="text-[14px] font-bold">
                        {item.description}
                      </div>
                    </div>
                    {item.errorCount != null && item.errorCount > 0 && (
                      <div
                        className="text-[13px] text-error-500 font-[500] cursor-pointer underline whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          item.onClickError?.();
                        }}
                      >
                        {item.errorCount} Submission Error
                        {item.errorCount !== 1 ? 's' : ''}
                      </div>
                    )}
                    <Icons.IconArrowRight />
                  </div>
                ))}
              </div>
            </AppCard>
          </Col>
        )}
      </Row>

      {employeeModalType && (
        <EmployeeModal
          isOpen={isOpenEmployeeModal}
          toggle={() => setIsOpenEmployeeModal(false)}
          type={employeeModalType}
        />
      )}
      {assignmentModalType && (
        <AssignmentModal
          isOpen={isOpenAssignmentModal}
          toggle={() => setIsOpenAssignmentModal(false)}
          type={assignmentModalType}
        />
      )}
      <SafeRecErrorModal
        isOpen={isOpenSafeRecErrorModal}
        toggle={() => setIsOpenSafeRecErrorModal(false)}
        cbAfterAction={() => runCompliance()}
      />
      <VeriPayeErrorModal
        isOpen={isOpenVeriPayeErrorModal}
        toggle={() => setIsOpenVeriPayeErrorModal(false)}
        cbAfterAction={() => runCompliance()}
      />
    </div>
  );
};

export default EmployeeSubcontractor;
